import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import type { GuestLookupIntent } from "src/stores/header/header.store";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { HamburgerMenu } from "src/icons/HamburgerMenu";
import { Logo } from "src/icons/Logo";
import { Search } from "src/icons/Search";
import { User } from "src/icons/User";
import { GiftIcon } from "@otrium/icons";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useVouchers } from "src/hooks/useVouchers";
import { isActiveNow } from "src/utils/vouchers";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { getStorageItem } from "src/utils/helper/localStorage";
import { OT_STORE_CREDIT_SEEN } from "src/utils/constants";
import styles from "./Header.module.scss";
import { Menu } from "src/atoms/Menu";
import { UserMenu } from "./UserMenu";
import { headerStore } from "src/stores/header/header.store";
import { Badge } from "src/atoms/Badge";
import { Button } from "src/atoms/Button";
import classNames from "classnames";
import Link from "next/link";
import { useUserWishlistMeta } from "src/hooks/useUserWishlistMeta";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { useRouter } from "next/router";
import { GenderSelect } from "./GenderSelect";
import { Heart } from "src/icons/Heart";
import { theme } from "src/theme";
import { SearchBlackIcon } from "src/icons";
import { useIsScrolledPast } from "src/hooks/useIsScrolledPast";
import { CartButton } from "./CartButton";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { Locale } from "src/lib/Intl";
import { locale } from "src/config/app-config";

type NavigationProps = {
  className?: string;
  gendersRef?: RefObject<HTMLDivElement>;
  serviceBannerRef?: RefObject<HTMLDivElement | null>;
  simplifiedHeader?: boolean;
  isLoginFlow?: boolean;
};

function Navigation({
  className,
  gendersRef,
  serviceBannerRef,
  simplifiedHeader = false,
  isLoginFlow = false,
}: NavigationProps): JSX.Element {
  const router = useRouter();
  const { data: userWishlistMetaData } = useUserWishlistMeta();
  const isLoggedIn = useIsLoggedIn();
  const { isMobile } = useBreakpoints();

  const { storeCreditActive } = useFeatureFlags();
  const { data: voucherData } = useVouchers(!storeCreditActive);

  const [guestLookupView, setGuestLookupView] =
    useState<GuestLookupIntent | null>(null);

  const handleOpenNavigationMenu = useCallback(
    () => headerStore.openNavigationMenu(),
    []
  );
  const handleOpenSearch = useCallback(() => headerStore.openSearch(), []);

  const showSearchButton = useIsScrolledPast(serviceBannerRef);

  const hasActiveCredit =
    voucherData?.vouchers?.some(
      (voucher) => voucher !== null && isActiveNow(voucher, new Date())
    ) ?? false;
  const showStoreCreditBadge = storeCreditActive && hasActiveCredit;

  const storeCreditSeen = getStorageItem(OT_STORE_CREDIT_SEEN) !== null;

  const showNotificationBadge =
    userWishlistMetaData?.userWishlistMetaData &&
    userWishlistMetaData?.userWishlistMetaData.wishlist_product_count > 0;

  return (
    <div className={classNames(styles.navigation, className)}>
      <div className={styles.container}>
        <Flex
          justifyContent={simplifiedHeader ? "center" : "space-between"}
          height={[58, 58, 58, 64]}
          placeItems="center"
        >
          <Flex flex="1" alignItems="center" gap="24px">
            {!simplifiedHeader ? (
              <>
                <Box display={["block", "block", "block", "none"]}>
                  <Button
                    className={styles.navigationButton}
                    data-testid="navigation-button"
                    onClick={handleOpenNavigationMenu}
                  >
                    <HamburgerMenu width="24" height="24" />
                  </Button>
                </Box>

                {!isLoginFlow ? (
                  <>
                    <Box display={["block", "block", "block", "none"]}>
                      <Button
                        className={styles.navigationButton}
                        data-testid="main-header-search-btn"
                        onClick={handleOpenSearch}
                      >
                        <Search stroke={theme.colors.tone.black} />
                      </Button>
                    </Box>

                    <Box display={["none", "none", "none", "block"]}>
                      <GenderSelect
                        locale={locale}
                        ref={gendersRef}
                        isInHeader
                      />
                    </Box>
                  </>
                ) : null}
              </>
            ) : null}
          </Flex>

          <HeaderLogo locale={locale} />

          <Flex flex="1" justifyContent="flex-end">
            {!simplifiedHeader && !isLoginFlow ? (
              <Flex
                justifyContent="flex-end"
                alignItems="center"
                sx={{
                  position: "relative",
                }}
                gap="24px"
              >
                {![
                  "/not-found",
                  "/my-profile",
                  "/my-orders",
                  "/my-o-credit",
                ].includes(router.route) && showSearchButton ? (
                  <Box display={["none", "none", "none", "block"]}>
                    <Button
                      className={styles.navigationButton}
                      data-testid="main-header-search-btn"
                      onClick={handleOpenSearch}
                    >
                      <SearchBlackIcon />
                    </Button>
                  </Box>
                ) : null}

                {showStoreCreditBadge && !isMobile && (
                  <Link
                    className={styles.navigationLink}
                    href={
                      isLoggedIn
                        ? "/my-o-credit"
                        : "/sign-in?redirect_to=/my-o-credit"
                    }
                    as={
                      isLoggedIn
                        ? "/my-o-credit"
                        : "/sign-in?redirect_to=/my-o-credit"
                    }
                    data-testid="main-header-o-credit-a"
                    prefetch={false}
                  >
                    <GiftIcon />
                    {showStoreCreditBadge && !storeCreditSeen && (
                      <Badge className={styles.notificationBadge} />
                    )}
                  </Link>
                )}

                <Box display={["none", "none", "block"]}>
                  <Menu>
                    <Menu.Button>
                      {({ onToggle }) => (
                        <Button
                          className={styles.navigationButton}
                          variant="secondary"
                          data-testid="main-header-my-account-button"
                          onClick={() => {
                            setGuestLookupView(null);
                            onToggle();
                          }}
                        >
                          <User />
                        </Button>
                      )}
                    </Menu.Button>
                    <Menu.List className={styles.userMenu}>
                      <UserMenu
                        guestLookupView={guestLookupView}
                        onGuestLookupViewChange={setGuestLookupView}
                      />
                    </Menu.List>
                  </Menu>
                </Box>

                <Link
                  className={styles.navigationLink}
                  href={
                    isLoggedIn
                      ? "/favourites"
                      : "/sign-in?redirect_to=/favourites"
                  }
                  as={
                    isLoggedIn
                      ? "/favourites"
                      : "/sign-in?redirect_to=/favourites"
                  }
                  data-testid="main-header-favourites-a"
                  prefetch={false}
                >
                  <Heart stroke={theme.colors.tone.black} strokeWidth="2" />
                  {showNotificationBadge && (
                    <Badge className={styles.notificationBadge} />
                  )}
                </Link>

                <CartButton />
              </Flex>
            ) : null}
          </Flex>
        </Flex>
      </div>
    </div>
  );
}

function HeaderLogo({ locale }: { locale: Locale }) {
  const { currentShopType } = useShopTypeContext();
  const ref = useRef(false);
  const [logoRouter, setLogoRouter] = useState(() =>
    currentShopType ? `/${localeFix({ gender: currentShopType })}` : "/"
  );

  useEffect(() => {
    if (!ref.current && currentShopType) {
      setLogoRouter(`/${localeFix({ gender: currentShopType })}`);
      ref.current = true;
    }
  }, [currentShopType]);

  return (
    <div className={styles.navigationLogo}>
      {ref.current ? (
        <Link
          href={getI18nRoute({
            route: `${logoRouter}`,
            locale,
          })}
          shallow={true}
        >
          <Box
            data-testid="main-header-logo"
            sx={{
              alignSelf: "center",
              display: "flex",
              justifyContent: "center",
              maxHeight: "100%",
            }}
          >
            <p className="sr-only">Otrium home page</p>
            <Logo width={98} />
          </Box>
        </Link>
      ) : (
        <Box
          data-testid="main-header-logo"
          sx={{
            alignSelf: "center",
            display: "flex",
            justifyContent: "center",
            maxHeight: "100%",
          }}
        >
          <p className="sr-only">Otrium home page</p>
          <Logo width={98} />
        </Box>
      )}
    </div>
  );
}

export { Navigation };
